import React from "react";
import { MapPin, ExternalLink, Phone, Clock } from "lucide-react";
import { Link } from "react-router-dom";

// Retailers data
const retailers = [
  {
    name: "Kroger",
    description: "One of America's largest grocery chains. POG Vodka available in the spirits aisle.",
    locations: "Multiple locations nationwide",
    website: "https://www.kroger.com",
    products: ["POG Vodka 750ml", "POG Vodka RTD"],
    type: "grocery",
  },
  {
    name: "Safeway",
    description: "Premium grocery with a curated spirits selection.",
    locations: "Western United States",
    website: "https://www.safeway.com",
    products: ["POG Vodka 750ml", "POG Vodka RTD"],
    type: "grocery",
  },
  {
    name: "Albertsons",
    description: "A trusted name in grocery with quality spirits departments.",
    locations: "Multiple locations across the US",
    website: "https://www.albertsons.com",
    products: ["POG Vodka 750ml", "POG Vodka RTD"],
    type: "grocery",
  },
  {
    name: "Foodland",
    description: "Hawaii's favorite grocery store. The home of POG.",
    locations: "Hawaii",
    website: "https://www.foodland.com",
    products: ["POG Vodka 750ml", "POG Vodka RTD 4-Pack", "POG Vodka RTD 12-Pack"],
    type: "grocery",
  },
];

const premiumLocations = [
  {
    name: "Premium Cigar Lounges",
    description: "Select cigar lounges carry Maduro Tequila, perfectly paired with your favorite smoke.",
    products: ["Maduro Reposado Oscuro"],
  },
  {
    name: "Specialty Liquor Stores",
    description: "Fine spirits retailers featuring our complete collection.",
    products: ["POG Vodka", "Maduro Tequila"],
  },
  {
    name: "High-End Bars & Restaurants",
    description: "Experience our spirits at premium on-premise establishments.",
    products: ["POG Vodka", "Maduro Tequila"],
  },
];

const WhereToBuy = () => {
  return (
    <div className="bg-[#050505] pt-20" data-testid="where-to-buy-page">
      {/* Hero */}
      <section className="py-24 sm:py-32">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto">
            <p className="font-subheading text-xs tracking-[0.3em] uppercase text-[#C8A876] mb-4">
              Find Us
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl tracking-tighter text-white mb-6">
              Where to Buy
            </h1>
            <p className="text-[#A3A3A3] text-lg">
              8eight8 Beverage Co products are available at select retailers. 
              Find POG Vodka and Maduro Tequila near you.
            </p>
          </div>
        </div>
      </section>

      {/* Main Retailers */}
      <section className="pb-24" data-testid="retailers-section">
        <div className="container-main">
          <div className="mb-12">
            <h2 className="font-heading text-3xl tracking-tight text-white mb-2">
              Grocery Partners
            </h2>
            <p className="text-[#A3A3A3]">
              POG Vodka is available at these major grocery chains
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {retailers.map((retailer) => (
              <div 
                key={retailer.name}
                className="group p-8 border border-white/5 bg-[#0A0A0A] hover:border-white/20 transition-all"
                data-testid={`retailer-card-${retailer.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-heading text-2xl text-white">{retailer.name}</h3>
                  <a 
                    href={retailer.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#C8A876] hover:text-[#DDBD90] transition-colors"
                    data-testid={`retailer-link-${retailer.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ExternalLink size={20} />
                  </a>
                </div>
                <p className="text-[#A3A3A3] text-sm mb-4">{retailer.description}</p>
                <div className="flex items-center gap-2 text-[#737373] text-sm mb-4">
                  <MapPin size={14} />
                  {retailer.locations}
                </div>
                <div className="flex flex-wrap gap-2">
                  {retailer.products.map((product) => (
                    <span 
                      key={product}
                      className="text-xs px-3 py-1 bg-[#1A1025] border border-[#3B1F54] text-[#FF6B35]"
                    >
                      {product}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium Locations */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="premium-locations-section">
        <div className="container-main">
          <div className="mb-12">
            <h2 className="font-heading text-3xl tracking-tight text-white mb-2">
              <span className="gradient-text-maduro">Premium Locations</span>
            </h2>
            <p className="text-[#A3A3A3]">
              Maduro Tequila is available at select premium venues
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {premiumLocations.map((location) => (
              <div 
                key={location.name}
                className="p-8 border border-[#333333] bg-[#1A1A1A]"
              >
                <h3 className="font-heading text-xl text-white mb-4">{location.name}</h3>
                <p className="text-[#A3A3A3] text-sm mb-4">{location.description}</p>
                <div className="flex flex-wrap gap-2">
                  {location.products.map((product) => (
                    <span 
                      key={product}
                      className="text-xs px-3 py-1 border border-[#C8A876]/30 text-[#C8A876]"
                    >
                      {product}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="contact-cta-section">
        <div className="container-main">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-heading text-3xl tracking-tight text-white mb-4">
              Can't Find Us Near You?
            </h2>
            <p className="text-[#A3A3A3] mb-8">
              We're expanding distribution. Contact us to request availability in your area 
              or to inquire about wholesale partnerships.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn-maduro" data-testid="contact-link-btn">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhereToBuy;
